/**
 * SA / 社員 用 報酬表・計算機 (v3.2.12 / SA-6 → v3.2.13 拡張)。
 *
 * URL: /sa/commissions
 *
 * - 「現在の有効セット」(commission_sets.isActive=true) のうち、
 *   自分のロール (referrer / sa / employee) に該当する list を表示
 *   - 外部ロール (level='external') → 'sa'
 *   - 社員ロール (level='employee' / 'leader' / 'owner') → 'employee'
 *
 * v3.2.13 拡張:
 * - 主軸カテゴリー選択 → 商品カタログを自動絞込
 * - 複数商品 + 数量の動的追加/削除
 * - 補足フィールド (固定比率% / 流動費率% / その他経費率% / 手数料額¥)
 * - 集計カード: 売上総額 / 合計報酬額 / 固定費 / 流動費 / その他経費 / 手数料 / 最終利益 / 利益率
 * - パイチャート (固定 / 流動 / その他 / 手数料 / 利益 = 100%)
 */
import type { CommissionItem, CommissionListType, CommissionType } from '@goenhub/shared';
import {
  COMMISSION_LIST_LABELS,
  COMMISSION_TYPE_LABELS,
  calculateCommission,
  calculateCommissionBreakdown,
  resolveEffectiveSelfDevelopedAdditionRate,
} from '@goenhub/shared';
import {
  AlertTriangle,
  Calculator,
  Coins,
  ExternalLink,
  FileDown,
  Plus,
  Table,
  Trash2,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';

import { EmptyState } from '@/components/common/EmptyState';
import { Screen } from '@/components/layout/Screen';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { useAdmin } from '@/hooks/useAdmins';
import { useAuth } from '@/hooks/useAuth';
import { useMyCommissionOverride } from '@/hooks/useCommissionOverrides';
import { useCommissionSetDetail, useCommissionSets } from '@/hooks/useCommissions';
import { useAllActiveCatalogItems, useProductCatalogs } from '@/hooks/useProductCatalogs';
import { useProductCategories } from '@/hooks/useProductCategories';
import { useSalesSettingsWithDefaults } from '@/hooks/useSettings';
import {
  buildPrintableHtml,
  CommissionTableBody,
  computeRowsCalc,
  defaultPdfColumnSelection,
  openPrintWindow,
  PdfColumnPicker,
  pdfColumnsForMode,
  REFERRAL_MODE_LABEL,
  type CatalogItemRow,
  type PdfColumnSelection,
  type ReferralViewMode,
} from '@/pages/commissions/CommissionTableView';
import { paths } from '@/routes/paths';

/**
 * 純粋ロールの確定マッピング。
 * - external → sa (固定、切替不可)
 * - employee → employee (固定、切替不可)
 * - leader / owner → null (管理者は試算用にセレクトで自由に選べる)
 */
function deriveFixedListType(level?: string): CommissionListType | null {
  if (level === 'external') return 'sa';
  if (level === 'employee') return 'employee';
  return null;
}

/** 管理者 (leader / owner) は 3 ロール横断で試算可能。 */
function isManagerRole(level?: string): boolean {
  return level === 'leader' || level === 'owner';
}

interface ProductRow {
  uid: string;
  productId: string;
  quantity: string;
}

const newRow = (): ProductRow => ({
  uid: Math.random().toString(36).slice(2),
  productId: '',
  quantity: '1',
});

const PIE_COLORS = {
  fixed: '#94a3b8',
  variable: '#a78bfa',
  other: '#fb923c',
  fee: '#f87171',
  profit: '#34d399',
} as const;

export function SaCommissionCalcPage() {
  const { user } = useAuth();
  const me = useAdmin(user?.uid);
  const sets = useCommissionSets();
  const { merged: salesSettings } = useSalesSettingsWithDefaults();

  // v3.2.23+: 季節モード切替。設定の seasonMode を初期値として、画面で通常/セール両方を切替表示できる。
  const [viewSeason, setViewSeason] = useState<'normal' | 'sale'>(
    salesSettings.seasonMode === 'sale' ? 'sale' : 'normal',
  );
  useEffect(() => {
    setViewSeason(salesSettings.seasonMode === 'sale' ? 'sale' : 'normal');
  }, [salesSettings.seasonMode]);

  // v3.2.23+: ProductCatalog から逆引きで「通常期 / セール期 の effective set」を決定。
  //   - 各 catalog (isActive=true) を season で振り分け、linkedCommissionSetId を取得
  //   - 複数ブランドがあれば最初の 1 件で代表 (現状の commission_sets は global single set 前提)
  const productCatalogsForLookup = useProductCatalogs();
  const { normalSet, saleSet } = useMemo(() => {
    const catalogsArr = productCatalogsForLookup.data ?? [];
    let normalLinked: string | null = null;
    let saleLinked: string | null = null;
    for (const c of catalogsArr) {
      if (!c.isActive) continue;
      const seas = c.season === 'sale' ? 'sale' : 'normal';
      const linked = c.linkedCommissionSetId ?? null;
      if (!linked) continue;
      if (seas === 'normal' && !normalLinked) normalLinked = linked;
      if (seas === 'sale' && !saleLinked) saleLinked = linked;
    }
    const setsArr = sets.data ?? [];
    return {
      normalSet: normalLinked ? (setsArr.find((s) => s.id === normalLinked) ?? null) : null,
      saleSet: saleLinked ? (setsArr.find((s) => s.id === saleLinked) ?? null) : null,
    };
  }, [productCatalogsForLookup.data, sets.data]);

  const activeSet = viewSeason === 'sale' ? saleSet : normalSet;

  const detail = useCommissionSetDetail(activeSet?.id);
  const allItems = useAllActiveCatalogItems();
  const categories = useProductCategories();
  // v3.2.17: 自分の override (SA / 社員 個別設定) を取得
  const myOverride = useMyCommissionOverride(activeSet?.id, user?.uid);

  const fixedListType = deriveFixedListType(me.data?.level);
  const manager = isManagerRole(me.data?.level);

  // 管理者の試算ロール (referrer / sa / employee の中から選択)
  const [managerListType, setManagerListType] = useState<CommissionListType | null>(null);

  // 管理者: 初回ロード時、データが入っているリストを自動選択（referrer → sa → employee）
  const autoSelectedManagerListType = useMemo<CommissionListType | null>(() => {
    if (!manager || !detail.data?.lists) return null;
    const order: CommissionListType[] = ['referrer', 'sa', 'employee'];
    for (const t of order) {
      if ((detail.data.lists[t]?.items ?? []).some((it) => it.value > 0)) return t;
    }
    return 'referrer';
  }, [manager, detail.data]);

  const effectiveManagerListType = managerListType ?? autoSelectedManagerListType;
  const myListType: CommissionListType | null = fixedListType ?? effectiveManagerListType;
  const myList = myListType ? (detail.data?.lists?.[myListType] ?? null) : null;

  // v3.2.17: タブ切替 (計算機 / 報酬表)
  const [topTab, setTopTab] = useState<'calculator' | 'table'>('calculator');

  // v3.2.29+: SA 報酬表の表示モード切替 (v3.3.0: ベース / 開拓顧客 / 並列)
  const [referralViewMode, setReferralViewMode] = useState<ReferralViewMode>('normal');

  // v3.3.0+: 計算機の顧客区分 (SA ロールのみ意味あり)
  //  - selfDeveloped: 開拓顧客 (弊社紹介フラグなし) → ベース報酬率 + 加算率
  //  - companyReferral: 弊社紹介 → ベース報酬率のまま (加算なし)
  const [calcCustomerType, setCalcCustomerType] = useState<'selfDeveloped' | 'companyReferral'>(
    'selfDeveloped',
  );

  // v3.3.0+: productId → categoryId のマップ (商品が属するカテゴリーを特定して加算率を引く)
  const productIdToCategoryId = useMemo(() => {
    const map = new Map<string, string>();
    const items = allItems.data ?? [];
    const cats = categories.data ?? [];
    for (const p of items) {
      const byCatalog = cats.find(
        (c) =>
          (c.catalogIds && c.catalogIds.includes(p.catalogId)) ||
          (c.linkedBrandNames && c.linkedBrandNames.includes(p.catalogCategory)),
      );
      if (byCatalog) {
        map.set(p.id, byCatalog.id);
        continue;
      }
      const byName = cats.find((c) => c.name === p.catalogCategory);
      if (byName) map.set(p.id, byName.id);
    }
    return map;
  }, [allItems.data, categories.data]);

  // 2026-11-02+: 自分の個別設定 (SA) に開拓顧客加算率があればそちらを使う
  const mySaOverride = myOverride.data?.recipientType === 'sa' ? myOverride.data : null;
  const hasOwnAdditionRate =
    !!mySaOverride &&
    (typeof mySaOverride.selfDevelopedAdditionRate === 'number' ||
      (mySaOverride.items ?? []).some(
        (it) => it.value > 0 && typeof it.selfDevelopedAdditionRate === 'number',
      ));

  /**
   * SA レコードでこの productId に適用される開拓顧客加算率 (%)。0 = 加算なし。
   * 個別設定の商品ごとの率 → 個別設定の既定 → セットのカテゴリー別加算率の順
   * (resolveEffectiveSelfDevelopedAdditionRate。旧「弊社紹介減額率」データは加算率として読み替え)。
   */
  const getAdditionRateForProduct = useCallback(
    (productId: string): number =>
      resolveEffectiveSelfDevelopedAdditionRate({
        set: activeSet,
        categoryId: productIdToCategoryId.get(productId) ?? null,
        overrideRate: mySaOverride?.selfDevelopedAdditionRate ?? null,
        itemRate:
          mySaOverride?.items?.find((it) => it.productId === productId)
            ?.selfDevelopedAdditionRate ?? null,
      }).rate,
    [productIdToCategoryId, activeSet, mySaOverride],
  );

  // v3.2.17: override の items が存在し value>0 のものがあれば、そちらを優先
  // 不足分は myList から補完しないで完全に override で置換 (個別設定の意図)
  const usingOverride = useMemo(() => {
    if (!myOverride.data) return false;
    return (myOverride.data.items ?? []).some((it) => it.value > 0);
  }, [myOverride.data]);

  // v3.2.29.5: 商品マスタにない productId (= 古いカタログの orphan) を表示から除外。
  // カタログ差替え時に commission_sets/{id}/lists/sa.items[] には旧 productId のエントリが残る。
  // 編集画面は categoryItems (現行) を起点にするため orphan を表示しないが、SA Portal は全件読込のため
  // フィルタなしだと orphan が表示され、税抜価格・減額率の参照が失敗する。
  const currentProductIds = useMemo(
    () => new Set((allItems.data ?? []).map((p) => p.id)),
    [allItems.data],
  );

  // 統計: orphan の件数を後段の通知に使う
  const rawEffectiveItems = useMemo(() => {
    if (usingOverride) return (myOverride.data?.items ?? []).filter((it) => it.value > 0);
    return (myList?.items ?? []).filter((it) => it.value > 0);
  }, [usingOverride, myOverride.data, myList]);

  const orphanCount = useMemo(
    () => rawEffectiveItems.filter((it) => !currentProductIds.has(it.productId)).length,
    [rawEffectiveItems, currentProductIds],
  );

  const effectiveItems: CommissionItem[] = useMemo(
    () => rawEffectiveItems.filter((it) => currentProductIds.has(it.productId)),
    [rawEffectiveItems, currentProductIds],
  );

  const effectiveDefaultType = usingOverride
    ? (myOverride.data?.defaultType ?? 'rate')
    : (myList?.defaultType ?? 'rate');

  const itemsByProductId = useMemo(() => {
    const map = new Map<string, CommissionItem>();
    for (const it of effectiveItems) {
      map.set(it.productId, it);
    }
    return map;
  }, [effectiveItems]);

  const eligibleCategories = useMemo(() => {
    const cats = categories.data ?? [];
    const merged = allItems.data ?? [];
    return cats
      .filter((c) => c.isActive && !c._deleted)
      .filter((c) => {
        const catalogIds = c.catalogIds && c.catalogIds.length > 0 ? c.catalogIds : null;
        const eligibleProducts = merged.filter((p) => {
          if (catalogIds) return catalogIds.includes(p.catalogId);
          return p.catalogCategory === c.name;
        });
        return eligibleProducts.some((p) => itemsByProductId.has(p.id));
      })
      .sort((a, b) => a.displayOrder - b.displayOrder);
  }, [categories.data, allItems.data, itemsByProductId]);

  const [selectedCategoryId, setSelectedCategoryId] = useState<string>('');

  const filteredProducts = useMemo(() => {
    if (!selectedCategoryId) return [];
    const cat = (categories.data ?? []).find((c) => c.id === selectedCategoryId);
    if (!cat) return [];
    const catalogIds = cat.catalogIds && cat.catalogIds.length > 0 ? cat.catalogIds : null;
    return (allItems.data ?? []).filter((p) => {
      if (!itemsByProductId.has(p.id)) return false;
      if (catalogIds) return catalogIds.includes(p.catalogId);
      return p.catalogCategory === cat.name;
    });
  }, [selectedCategoryId, categories.data, allItems.data, itemsByProductId]);

  const [rows, setRows] = useState<ProductRow[]>([newRow()]);

  const [fixedRate, setFixedRate] = useState<string>('0');
  const [variableRate, setVariableRate] = useState<string>('0');
  const [otherRate, setOtherRate] = useState<string>('0');
  const [fee, setFee] = useState<string>('0');

  const calc = useMemo(() => {
    if (!myList) return null;
    let grossRevenue = 0;
    let commissionTotal = 0;
    const breakdown: Array<{
      productName: string;
      qty: number;
      unitPriceExcludingTax: number;
      subtotalPrice: number;
      commissionAmount: number;
      formula: string;
    }> = [];

    for (const row of rows) {
      if (!row.productId) continue;
      const item = itemsByProductId.get(row.productId);
      if (!item) continue;
      const product = (allItems.data ?? []).find((p) => p.id === row.productId);
      if (!product) continue;
      const qty = Math.max(1, Math.floor(Number(row.quantity) || 1));
      const subtotal = product.priceExcludingTax * qty;
      grossRevenue += subtotal;
      // v3.3.0+: SA ロール & 開拓顧客区分のときのみカテゴリー別加算率を適用
      const additionRate =
        myListType === 'sa' && calcCustomerType === 'selfDeveloped'
          ? getAdditionRateForProduct(row.productId)
          : 0;
      const r = calculateCommission({
        basePriceExcludingTax: subtotal,
        item,
        defaultType: effectiveDefaultType,
        ...(additionRate > 0 ? { selfDevelopedAdditionRate: additionRate } : {}),
      });
      commissionTotal += r.finalAmount;
      breakdown.push({
        productName: product.name,
        qty,
        unitPriceExcludingTax: product.priceExcludingTax,
        subtotalPrice: subtotal,
        commissionAmount: r.finalAmount,
        formula: r.formula,
      });
    }

    const fixedNum = Math.max(0, Number(fixedRate) || 0);
    const variableNum = Math.max(0, Number(variableRate) || 0);
    const otherNum = Math.max(0, Number(otherRate) || 0);
    const feeNum = Math.max(0, Number(fee) || 0);

    const result = calculateCommissionBreakdown({
      grossRevenue,
      commissionTotal,
      fixedRate: fixedNum,
      variableRate: variableNum,
      otherRate: otherNum,
      fee: feeNum,
    });

    return { breakdown, result };
  }, [
    rows,
    itemsByProductId,
    allItems.data,
    myList,
    fixedRate,
    variableRate,
    otherRate,
    fee,
    myListType,
    calcCustomerType,
    getAdditionRateForProduct,
  ]);

  const pieData = useMemo(() => {
    if (!calc || calc.result.commissionTotal <= 0) return [];
    const { fixedCost, variableCost, otherCost, fee: feeVal, netProfit } = calc.result;
    const profitValue = Math.max(0, netProfit);
    return [
      { name: '固定費', value: fixedCost, color: PIE_COLORS.fixed },
      { name: '流動費', value: variableCost, color: PIE_COLORS.variable },
      { name: 'その他経費', value: otherCost, color: PIE_COLORS.other },
      { name: '手数料', value: feeVal, color: PIE_COLORS.fee },
      { name: '利益', value: profitValue, color: PIE_COLORS.profit },
    ].filter((d) => d.value > 0);
  }, [calc]);

  const addRow = () => setRows((prev) => [...prev, newRow()]);
  const removeRow = (uid: string) =>
    setRows((prev) => (prev.length <= 1 ? prev : prev.filter((r) => r.uid !== uid)));
  const updateRow = (uid: string, patch: Partial<ProductRow>) =>
    setRows((prev) => prev.map((r) => (r.uid === uid ? { ...r, ...patch } : r)));

  if (
    sets.isLoading ||
    detail.isLoading ||
    allItems.isLoading ||
    me.isLoading ||
    categories.isLoading
  ) {
    return (
      <Screen title="読み込み中…">
        <Spinner />
      </Screen>
    );
  }

  if (!activeSet) {
    return (
      <Screen title="報酬表" description="現在の有効報酬セットを表示します。">
        <EmptyState
          icon={Coins}
          title="有効な報酬セットがありません"
          description="管理者が報酬設定を有効化するとここに表示されます。"
        />
      </Screen>
    );
  }

  if (!myListType) {
    return (
      <Screen title="報酬表" description={activeSet.name}>
        <EmptyState
          icon={Coins}
          title="報酬対象ロールではありません"
          description="あなたのアカウントは報酬計算の対象外です。"
        />
      </Screen>
    );
  }

  const description = manager
    ? `現在の有効セット: ${activeSet.name} / 試算ロール: ${COMMISSION_LIST_LABELS[myListType]} (管理者として切替可能)`
    : `現在の有効セット: ${activeSet.name} / あなたのロール: ${COMMISSION_LIST_LABELS[myListType]}`;

  const effectiveSeason = salesSettings.seasonMode === 'sale' ? 'sale' : 'normal';

  return (
    <Screen title="報酬表 / 計算機" description={description}>
      <div className="space-y-5">
        {/* v3.2.23+: 季節モード切替 (通常期 / セール期 を常に並列確認可能) */}
        <div className="flex items-center gap-3 rounded-md border border-[var(--border)] bg-[var(--bg-elevated)] p-3">
          <span className="text-xs font-semibold text-[var(--text-secondary)]">表示中の報酬:</span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setViewSeason('normal')}
              className={`px-3 py-1 text-xs rounded-md border transition-colors ${
                viewSeason === 'normal'
                  ? 'bg-[var(--accent)] text-white border-[var(--accent)] font-semibold'
                  : 'bg-[var(--bg-input)] border-[var(--border)] hover:bg-[var(--bg-hover)]'
              }`}
              disabled={!normalSet}
              title={!normalSet ? '通常期のセットが未設定です' : undefined}
            >
              通常期
              {normalSet ? ` (${normalSet.name})` : ' (未設定)'}
              {effectiveSeason === 'normal' && <span className="ml-1.5 text-[10px]">★ 適用中</span>}
            </button>
            <button
              type="button"
              onClick={() => setViewSeason('sale')}
              className={`px-3 py-1 text-xs rounded-md border transition-colors ${
                viewSeason === 'sale'
                  ? 'bg-amber-600 text-white border-amber-600 font-semibold'
                  : 'bg-[var(--bg-input)] border-[var(--border)] hover:bg-[var(--bg-hover)]'
              }`}
              disabled={!saleSet}
              title={!saleSet ? 'セール期のセットが未設定です' : undefined}
            >
              セール期
              {saleSet ? ` (${saleSet.name})` : ' (未設定)'}
              {effectiveSeason === 'sale' && <span className="ml-1.5 text-[10px]">★ 適用中</span>}
            </button>
          </div>
          {viewSeason !== effectiveSeason && (
            <span className="text-[11px] text-amber-700">
              ※ 試算用表示。実際の入金時はシステム全体の「
              {effectiveSeason === 'sale' ? 'セール期' : '通常期'}」設定が適用されます。
            </span>
          )}
        </div>

        {/* v3.2.29.5: orphan 件数の通知 (管理者のみ) */}
        {manager && activeSet && orphanCount > 0 && (
          <div className="rounded-md border border-amber-300/40 bg-amber-50/40 dark:bg-amber-950/15 p-2 text-[11px]">
            <span className="font-semibold text-amber-700">
              ⚠️ {orphanCount} 件の旧データを表示から除外しました
            </span>
            <span className="text-[10px] text-[var(--text-muted)] ml-2">
              ({COMMISSION_LIST_LABELS[myListType]} リストに、現行商品マスタにない productId
              のエントリがあります — 過去のカタログ差替え時の残骸。報酬計算には影響しません)
            </span>
          </div>
        )}

        {/* v3.2.17: タブ切替 */}
        <div className="flex items-center gap-1 rounded-md border border-[var(--border)] bg-[var(--bg-surface)] p-1">
          {(
            [
              { value: 'calculator', label: '計算機', icon: Calculator },
              { value: 'table', label: '報酬表', icon: Table },
            ] as const
          ).map((t) => (
            <button
              key={t.value}
              type="button"
              onClick={() => setTopTab(t.value)}
              className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs rounded transition-colors ${
                topTab === t.value
                  ? 'bg-[var(--accent-subtle)] text-[var(--accent)] font-medium'
                  : 'text-[var(--text-secondary)] hover:bg-[var(--bg-hover)]'
              }`}
            >
              <t.icon className="size-3.5" />
              {t.label}
            </button>
          ))}
        </div>

        {topTab === 'calculator' && (
          <div className="rounded-md border border-[var(--border)] bg-[var(--bg-surface)] p-4 space-y-4">
            <div className="flex items-center gap-2">
              <Calculator className="size-4 text-[var(--accent)]" />
              <h3 className="text-sm font-semibold">報酬計算機</h3>
            </div>

            {manager && (
              <div>
                <label className="block text-[10px] text-[var(--text-muted)] mb-1">
                  試算ロール (管理者は 3 ロール横断で試算できます)
                </label>
                <select
                  value={myListType ?? ''}
                  onChange={(e) => {
                    setManagerListType(e.target.value as CommissionListType);
                    setSelectedCategoryId('');
                    setRows([newRow()]);
                    setFixedRate('0');
                    setVariableRate('0');
                    setOtherRate('0');
                    setFee('0');
                  }}
                  className="w-full px-3 py-2 text-xs rounded-sm border border-[var(--border)] bg-[var(--bg-input)]"
                >
                  <option value="referrer">{COMMISSION_LIST_LABELS.referrer}</option>
                  <option value="sa">{COMMISSION_LIST_LABELS.sa}</option>
                  <option value="employee">{COMMISSION_LIST_LABELS.employee}</option>
                </select>
              </div>
            )}

            {/* v3.3.0+: 顧客区分トグル (SA ロールのみ。開拓顧客=加算あり / 弊社紹介=ベース) */}
            {myListType === 'sa' && (
              <div className="rounded-md border border-emerald-300/40 bg-emerald-50/30 dark:bg-emerald-950/10 p-2">
                <label className="block text-[10px] text-[var(--text-muted)] mb-1">
                  顧客区分 (SA 報酬のみ影響)
                </label>
                <div className="flex items-center gap-2 flex-wrap">
                  {(
                    [
                      { value: 'selfDeveloped', label: '開拓顧客 (加算あり)' },
                      { value: 'companyReferral', label: '弊社紹介 (ベース)' },
                    ] as const
                  ).map((t) => (
                    <button
                      key={t.value}
                      type="button"
                      onClick={() => setCalcCustomerType(t.value)}
                      className={`px-2.5 py-1 text-[11px] rounded-md border transition-colors ${
                        calcCustomerType === t.value
                          ? 'bg-[var(--accent)] text-white border-[var(--accent)] font-semibold'
                          : 'bg-[var(--bg-input)] border-[var(--border)] hover:bg-[var(--bg-hover)]'
                      }`}
                    >
                      {t.label}
                    </button>
                  ))}
                  <span className="text-[10px] text-[var(--text-muted)]">
                    {hasOwnAdditionRate
                      ? '開拓顧客 = 弊社紹介フラグのない商談。あなたの個別設定の「開拓顧客加算率」がベース報酬率に加算されます。'
                      : '開拓顧客 = 弊社紹介フラグのない商談。カテゴリー別「開拓顧客加算率」がベース報酬率に加算されます。'}
                  </span>
                </div>
              </div>
            )}

            <div>
              <label className="block text-[10px] text-[var(--text-muted)] mb-1">
                主軸カテゴリー
              </label>
              <select
                value={selectedCategoryId}
                onChange={(e) => {
                  setSelectedCategoryId(e.target.value);
                  setRows([newRow()]);
                }}
                className="w-full px-3 py-2 text-xs rounded-sm border border-[var(--border)] bg-[var(--bg-input)]"
              >
                <option value="">カテゴリーを選択してください</option>
                {eligibleCategories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
              {eligibleCategories.length === 0 && (
                <div className="mt-1 space-y-1">
                  <p className="text-[10px] text-[var(--text-muted)]">
                    {myListType ? COMMISSION_LIST_LABELS[myListType] : ''}
                    リストに商品報酬が設定されていません。
                    {manager
                      ? '報酬設定画面 → 有効セット → カテゴリーカード → 該当ロールタブで値を設定してください。'
                      : '管理者に報酬設定を依頼してください。'}
                  </p>
                  {manager && (
                    <Link
                      to={paths.commissions}
                      className="inline-flex items-center gap-1 text-[11px] text-[var(--accent)] hover:underline"
                    >
                      <ExternalLink className="size-3" />
                      報酬設定を開く
                    </Link>
                  )}
                </div>
              )}
            </div>

            {selectedCategoryId && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] text-[var(--text-muted)] uppercase tracking-wider">
                    商品 ({rows.length} 行)
                  </span>
                  <Button type="button" variant="secondary" size="sm" onClick={addRow}>
                    <Plus className="size-3.5" />
                    行追加
                  </Button>
                </div>
                {rows.map((row) => (
                  <div key={row.uid} className="grid grid-cols-[1fr_90px_36px] gap-2 items-end">
                    <div>
                      <select
                        value={row.productId}
                        onChange={(e) => updateRow(row.uid, { productId: e.target.value })}
                        className="w-full px-3 py-2 text-xs rounded-sm border border-[var(--border)] bg-[var(--bg-input)]"
                      >
                        <option value="">商品を選択</option>
                        {filteredProducts.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.name} (税抜 ¥{p.priceExcludingTax.toLocaleString()})
                          </option>
                        ))}
                      </select>
                    </div>
                    <input
                      type="number"
                      min={1}
                      value={row.quantity}
                      onChange={(e) => updateRow(row.uid, { quantity: e.target.value })}
                      placeholder="数量"
                      className="px-3 py-2 text-xs rounded-sm border border-[var(--border)] bg-[var(--bg-input)] tabular-nums"
                    />
                    <button
                      type="button"
                      onClick={() => removeRow(row.uid)}
                      disabled={rows.length <= 1}
                      className="size-9 inline-flex items-center justify-center rounded-sm border border-[var(--border)] bg-[var(--bg-input)] text-[var(--text-muted)] hover:text-[var(--accent-rose,_#f87171)] disabled:opacity-40 disabled:cursor-not-allowed"
                      title="この行を削除"
                    >
                      <Trash2 className="size-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {selectedCategoryId && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 border-t border-[var(--border)] pt-3">
                <div>
                  <label className="block text-[10px] text-[var(--text-muted)] mb-1">
                    固定比率 (%)
                  </label>
                  <input
                    type="number"
                    min={0}
                    max={100}
                    step="0.1"
                    value={fixedRate}
                    onChange={(e) => setFixedRate(e.target.value)}
                    className="w-full px-3 py-2 text-xs rounded-sm border border-[var(--border)] bg-[var(--bg-input)] tabular-nums"
                  />
                </div>
                <div>
                  <label className="block text-[10px] text-[var(--text-muted)] mb-1">
                    流動費率 (%)
                  </label>
                  <input
                    type="number"
                    min={0}
                    max={100}
                    step="0.1"
                    value={variableRate}
                    onChange={(e) => setVariableRate(e.target.value)}
                    className="w-full px-3 py-2 text-xs rounded-sm border border-[var(--border)] bg-[var(--bg-input)] tabular-nums"
                  />
                </div>
                <div>
                  <label className="block text-[10px] text-[var(--text-muted)] mb-1">
                    その他経費率 (%)
                  </label>
                  <input
                    type="number"
                    min={0}
                    max={100}
                    step="0.1"
                    value={otherRate}
                    onChange={(e) => setOtherRate(e.target.value)}
                    className="w-full px-3 py-2 text-xs rounded-sm border border-[var(--border)] bg-[var(--bg-input)] tabular-nums"
                  />
                </div>
                <div>
                  <label className="block text-[10px] text-[var(--text-muted)] mb-1">
                    手数料額 (¥)
                  </label>
                  <input
                    type="number"
                    min={0}
                    step="1"
                    value={fee}
                    onChange={(e) => setFee(e.target.value)}
                    className="w-full px-3 py-2 text-xs rounded-sm border border-[var(--border)] bg-[var(--bg-input)] tabular-nums"
                  />
                </div>
              </div>
            )}

            {calc && calc.result.commissionTotal > 0 && (
              <div className="border-t border-[var(--border)] pt-3 space-y-3">
                {calc.result.netProfit < 0 && (
                  <div className="flex items-center gap-2 rounded-sm border border-[var(--accent-rose,_#f87171)] bg-[var(--accent-rose-subtle,_#fee2e2)] p-2 text-[11px] text-[var(--accent-rose,_#dc2626)]">
                    <AlertTriangle className="size-3.5" />
                    経費・手数料の合計が報酬額を超えています（赤字）。
                  </div>
                )}

                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  <SummaryCard
                    label="売上総額 (税抜)"
                    value={calc.result.grossRevenue}
                    accent="muted"
                  />
                  <SummaryCard
                    label="合計報酬額"
                    value={calc.result.commissionTotal}
                    accent="primary"
                  />
                  <SummaryCard label="固定費" value={calc.result.fixedCost} accent="muted" />
                  <SummaryCard label="流動費" value={calc.result.variableCost} accent="muted" />
                  <SummaryCard label="その他経費" value={calc.result.otherCost} accent="muted" />
                  <SummaryCard label="手数料" value={calc.result.fee} accent="muted" />
                  <SummaryCard
                    label="最終利益"
                    value={calc.result.netProfit}
                    accent={calc.result.netProfit >= 0 ? 'success' : 'danger'}
                  />
                  <SummaryCard
                    label="利益率"
                    value={calc.result.profitRate}
                    isPercent
                    accent={calc.result.profitRate > 0 ? 'success' : 'danger'}
                  />
                </div>

                {pieData.length > 0 && (
                  <div className="rounded-md border border-[var(--border)] bg-[var(--bg-elevated)] p-3">
                    <h4 className="text-[11px] font-medium text-[var(--text-muted)] mb-2">
                      内訳 (合計報酬額 100% に対する構成)
                    </h4>
                    <div style={{ width: '100%', height: 260 }}>
                      <ResponsiveContainer>
                        <PieChart>
                          <Pie
                            data={pieData}
                            dataKey="value"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            outerRadius={80}
                            label={(d: { name?: string; value?: number }) =>
                              `${d.name ?? ''} ${
                                calc.result.commissionTotal > 0
                                  ? Math.round(((d.value ?? 0) / calc.result.commissionTotal) * 100)
                                  : 0
                              }%`
                            }
                            labelLine={false}
                          >
                            {pieData.map((d, i) => (
                              <Cell key={i} fill={d.color} />
                            ))}
                          </Pie>
                          <Tooltip
                            formatter={(value: number, name: string) => [
                              `¥${value.toLocaleString()}`,
                              name,
                            ]}
                          />
                          <Legend
                            verticalAlign="bottom"
                            iconSize={10}
                            wrapperStyle={{ fontSize: '11px' }}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                )}

                {calc.breakdown.length > 0 && (
                  <details className="rounded-md border border-[var(--border)] bg-[var(--bg-elevated)] p-3">
                    <summary className="cursor-pointer text-[11px] font-medium text-[var(--text-muted)]">
                      商品ごとの計算明細
                    </summary>
                    <div className="mt-2 space-y-1.5 text-[11px]">
                      {calc.breakdown.map((b, i) => (
                        <div key={i} className="border-t border-[var(--border)] pt-1.5">
                          <div className="font-medium">
                            {b.productName} × {b.qty}
                          </div>
                          <div className="text-[var(--text-muted)]">{b.formula}</div>
                        </div>
                      ))}
                    </div>
                  </details>
                )}
              </div>
            )}
          </div>
        )}

        {topTab === 'table' && (
          <SaCommissionTable
            activeSetName={activeSet.name}
            listTypeLabel={COMMISSION_LIST_LABELS[myListType]}
            effectiveDefaultType={effectiveDefaultType}
            effectiveItems={effectiveItems}
            usingOverride={usingOverride}
            briefingFee={myOverride.data?.briefingFee ?? null}
            allItems={allItems.data ?? []}
            isSaRole={myListType === 'sa'}
            referralViewMode={referralViewMode}
            setReferralViewMode={setReferralViewMode}
            getAdditionRateForProduct={getAdditionRateForProduct}
            hasOwnAdditionRate={hasOwnAdditionRate}
            manager={manager}
          />
        )}
      </div>
    </Screen>
  );
}

// ─────────────────────────────────────────────
// v3.2.29+: SA 報酬表 (表示モード切替 + PDF 出力 / v3.3.0: 加算方式 + PDF カラム選択)
// ─────────────────────────────────────────────

function SaCommissionTable({
  activeSetName,
  listTypeLabel,
  effectiveDefaultType,
  effectiveItems,
  usingOverride,
  briefingFee,
  allItems,
  isSaRole,
  referralViewMode,
  setReferralViewMode,
  getAdditionRateForProduct,
  hasOwnAdditionRate,
  manager,
}: {
  activeSetName: string;
  listTypeLabel: string;
  effectiveDefaultType: CommissionType;
  effectiveItems: CommissionItem[];
  usingOverride: boolean;
  briefingFee: number | null;
  allItems: CatalogItemRow[];
  isSaRole: boolean;
  referralViewMode: ReferralViewMode;
  setReferralViewMode: (m: ReferralViewMode) => void;
  getAdditionRateForProduct: (productId: string) => number;
  /** 自分の個別設定に開拓顧客加算率があるか (注記の出し分け用) */
  hasOwnAdditionRate: boolean;
  manager: boolean;
}) {
  // 「開拓顧客」モード切替は SA ロールのときのみ意味あり (紹介者・社員は影響なし)
  const showReferralModes = isSaRole;

  // v3.3.0+: PDF 出力カラム選択
  const [pdfColumns, setPdfColumns] = useState<PdfColumnSelection>(defaultPdfColumnSelection);

  const modeForRender: ReferralViewMode = showReferralModes ? referralViewMode : 'normal';

  // 行ごとの計算結果を memo 化（PDF 出力でも再利用）
  const rowsCalc = useMemo(
    () =>
      computeRowsCalc({
        items: effectiveItems,
        allItems,
        defaultType: effectiveDefaultType,
        isSaRole,
        getAdditionRateForProduct,
      }),
    [effectiveItems, allItems, effectiveDefaultType, isSaRole, getAdditionRateForProduct],
  );

  const handleExportPdf = () => {
    const extraNotes: string[] = [];
    if (usingOverride) extraNotes.push('個別設定（override）適用中');
    if (usingOverride && briefingFee && briefingFee > 0) {
      extraNotes.push(`個別説明代行料: ¥${briefingFee.toLocaleString()} / 件`);
    }
    const html = buildPrintableHtml({
      title: `報酬表 ${activeSetName} / ${listTypeLabel} (${REFERRAL_MODE_LABEL[modeForRender]})`,
      metaLines: [
        `セット: ${activeSetName}`,
        `対象: ${listTypeLabel}`,
        `表示モード: ${REFERRAL_MODE_LABEL[modeForRender]}`,
      ],
      mode: modeForRender,
      rows: rowsCalc,
      extraNotes,
      columns: pdfColumnsForMode(modeForRender).filter((k) => pdfColumns[k]),
    });
    openPrintWindow(html);
  };

  return (
    <div>
      <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <h3 className="text-sm font-semibold">
            報酬表 (既定タイプ: {COMMISSION_TYPE_LABELS[effectiveDefaultType]})
          </h3>
          {usingOverride && (
            <Badge variant="accent" className="text-[10px]">
              個別設定適用中
            </Badge>
          )}
          {usingOverride && briefingFee != null && briefingFee > 0 && (
            <span className="text-[10px] text-[var(--text-muted)]">
              個別説明代行料: ¥{briefingFee.toLocaleString()} / 件
            </span>
          )}
        </div>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={handleExportPdf}
          disabled={effectiveItems.length === 0}
          title="現在の表示モードを PDF として出力"
        >
          <FileDown className="size-3.5" />
          PDF 出力
        </Button>
      </div>

      {showReferralModes && (
        <div className="mb-3 flex items-center gap-2 rounded-md border border-emerald-300/40 bg-emerald-50/30 dark:bg-emerald-950/10 p-2 flex-wrap">
          <span className="text-[11px] font-medium text-[var(--text-secondary)]">表示モード:</span>
          {(
            [
              { value: 'normal', label: 'ベース' },
              { value: 'selfDeveloped', label: '開拓顧客' },
              { value: 'parallel', label: '並列' },
            ] as const
          ).map((m) => (
            <button
              key={m.value}
              type="button"
              onClick={() => setReferralViewMode(m.value)}
              className={`px-2.5 py-1 text-[11px] rounded-md border transition-colors ${
                referralViewMode === m.value
                  ? 'bg-[var(--accent)] text-white border-[var(--accent)] font-semibold'
                  : 'bg-[var(--bg-input)] border-[var(--border)] hover:bg-[var(--bg-hover)]'
              }`}
            >
              {m.label}
            </button>
          ))}
          <span className="text-[10px] text-[var(--text-muted)] ml-2">
            {hasOwnAdditionRate
              ? '「開拓顧客」モードは、あなたの個別設定の「開拓顧客加算率」をベース報酬率に加算した額です (弊社紹介の商談はベースのまま)'
              : '「開拓顧客」モードは、報酬設定 > SA > カテゴリー別「開拓顧客加算率」をベース報酬率に加算した額です (弊社紹介の商談はベースのまま)'}
          </span>
        </div>
      )}

      {/* v3.3.0+: PDF 出力カラム選択 */}
      {effectiveItems.length > 0 && (
        <div className="mb-3">
          <PdfColumnPicker mode={modeForRender} selection={pdfColumns} onChange={setPdfColumns} />
        </div>
      )}

      {effectiveItems.length === 0 ? (
        <div className="space-y-2">
          <EmptyState
            icon={Coins}
            title="報酬設定がありません"
            description={
              manager
                ? '報酬設定画面 → 有効セット → カテゴリーカード → 該当ロールタブで値を設定してください。'
                : 'このセットに、あなたのロール用の商品報酬がまだ設定されていません。管理者に依頼してください。'
            }
          />
          {manager && (
            <div className="text-center">
              <Link
                to={paths.commissions}
                className="inline-flex items-center gap-1 text-xs text-[var(--accent)] hover:underline"
              >
                <ExternalLink className="size-3.5" />
                報酬設定を開く
              </Link>
            </div>
          )}
        </div>
      ) : (
        <CommissionTableBody rows={rowsCalc} mode={modeForRender} />
      )}
    </div>
  );
}

function SummaryCard({
  label,
  value,
  accent,
  isPercent,
}: {
  label: string;
  value: number;
  accent: 'muted' | 'primary' | 'success' | 'danger';
  isPercent?: boolean;
}) {
  const accentClass =
    accent === 'primary'
      ? 'text-[var(--accent)]'
      : accent === 'success'
        ? 'text-[var(--accent-emerald,_#10b981)]'
        : accent === 'danger'
          ? 'text-[var(--accent-rose,_#f87171)]'
          : '';
  return (
    <div className="rounded-sm border border-[var(--border)] bg-[var(--bg-input)] p-2">
      <div className="text-[10px] text-[var(--text-muted)]">{label}</div>
      <div className={`mt-0.5 text-sm font-semibold tabular-nums ${accentClass}`}>
        {isPercent
          ? `${value.toFixed(1)}%`
          : `${value < 0 ? '−' : ''}¥${Math.abs(value).toLocaleString()}`}
      </div>
    </div>
  );
}
